// src/contexts/SchoolConfigContext.tsx
// Context provider for school configuration - centralizes all school-specific settings

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  SchoolConfiguration,
  CurriculumArea,
  LevelCode,
  ConfigurableGridType,
  DEFAULT_SCHOOL_CONFIG,
  DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
  DEFAULT_MINUTES_PER_LINE,
} from '../types/schoolConfig';
import {
  fetchSchoolConfig,
  updateSchoolConfig as updateSchoolConfigService,
  subscribeToSchoolConfig,
  fetchCurriculumAreas,
  fetchLevelCodes,
  fetchGridTypes,
  subscribeToCurriculumAreas,
  subscribeToLevelCodes,
  subscribeToGridTypes,
  createCurriculumArea as createCurriculumAreaService,
  updateCurriculumArea as updateCurriculumAreaService,
  deleteCurriculumArea as deleteCurriculumAreaService,
  createLevelCode as createLevelCodeService,
  updateLevelCode as updateLevelCodeService,
  deleteLevelCode as deleteLevelCodeService,
  buildCurriculumAreaMap,
  buildLevelCodeMap,
} from '../services/schoolConfigService';

// ============================================================================
// Context Types
// ============================================================================

interface SchoolConfigContextType {
  // School Configuration
  config: SchoolConfiguration;
  updateConfig: (updates: Partial<SchoolConfiguration>) => Promise<void>;

  // Curriculum Areas
  curriculumAreas: CurriculumArea[];
  curriculumAreaMap: Map<string, CurriculumArea>;
  createCurriculumArea: (area: Omit<CurriculumArea, 'id' | 'createdAt' | 'updatedAt'>) => Promise<CurriculumArea>;
  updateCurriculumArea: (id: string, updates: Partial<CurriculumArea>) => Promise<CurriculumArea>;
  deleteCurriculumArea: (id: string) => Promise<void>;

  // Level Codes
  levelCodes: LevelCode[];
  levelCodeMap: Map<string, LevelCode>;
  createLevelCode: (level: Omit<LevelCode, 'id' | 'createdAt' | 'updatedAt'>) => Promise<LevelCode>;
  updateLevelCode: (id: string, updates: Partial<LevelCode>) => Promise<LevelCode>;
  deleteLevelCode: (id: string) => Promise<void>;

  // Grid Types
  gridTypes: ConfigurableGridType[];

  // Menu Visibility
  hiddenMenuItems: string[];
  isMenuItemHidden: (path: string) => boolean;
  hideMenuItem: (path: string) => Promise<void>;
  showMenuItem: (path: string) => Promise<void>;
  setHiddenMenuItems: (items: string[]) => Promise<void>;

  // Convenience accessors for common values
  baseMinutesPerFortnight: number;
  defaultMinutesPerLine: number;
  schoolName: string;
  schoolShortName: string;
  bannerUrl: string;
  principalName: string;
  yearLevels: number[];

  // Loading state
  loading: boolean;
  error: string | null;

  // Refresh data
  refresh: () => Promise<void>;
}

// ============================================================================
// Context Creation
// ============================================================================

const SchoolConfigContext = createContext<SchoolConfigContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface SchoolConfigProviderProps {
  children: ReactNode;
}

export const SchoolConfigProvider: React.FC<SchoolConfigProviderProps> = ({ children }) => {
  // State
  const [config, setConfig] = useState<SchoolConfiguration>(DEFAULT_SCHOOL_CONFIG);
  const [curriculumAreas, setCurriculumAreas] = useState<CurriculumArea[]>([]);
  const [levelCodes, setLevelCodes] = useState<LevelCode[]>([]);
  const [gridTypes, setGridTypes] = useState<ConfigurableGridType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derived state - lookup maps
  const curriculumAreaMap = React.useMemo(
    () => buildCurriculumAreaMap(curriculumAreas),
    [curriculumAreas]
  );
  const levelCodeMap = React.useMemo(
    () => buildLevelCodeMap(levelCodes),
    [levelCodes]
  );

  // Load all configuration data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [configData, areasData, levelsData, typesData] = await Promise.all([
        fetchSchoolConfig(),
        fetchCurriculumAreas(),
        fetchLevelCodes(),
        fetchGridTypes(),
      ]);

      setConfig(configData);
      setCurriculumAreas(areasData);
      setLevelCodes(levelsData);
      setGridTypes(typesData);
    } catch (err) {
      console.error('Error loading school configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set up real-time subscriptions
  useEffect(() => {
    const unsubConfig = subscribeToSchoolConfig((newConfig) => {
      setConfig(newConfig);
    });

    const unsubAreas = subscribeToCurriculumAreas((newAreas) => {
      setCurriculumAreas(newAreas);
    });

    const unsubLevels = subscribeToLevelCodes((newLevels) => {
      setLevelCodes(newLevels);
    });

    const unsubTypes = subscribeToGridTypes((newTypes) => {
      setGridTypes(newTypes);
    });

    return () => {
      unsubConfig();
      unsubAreas();
      unsubLevels();
      unsubTypes();
    };
  }, []);

  // Update school configuration
  const updateConfig = useCallback(async (updates: Partial<SchoolConfiguration>) => {
    try {
      const updatedConfig = await updateSchoolConfigService(updates);
      setConfig(updatedConfig);
    } catch (err) {
      console.error('Error updating configuration:', err);
      throw err;
    }
  }, []);

  // Curriculum Area CRUD
  const createCurriculumArea = useCallback(
    async (area: Omit<CurriculumArea, 'id' | 'createdAt' | 'updatedAt'>) => {
      const newArea = await createCurriculumAreaService(area);
      setCurriculumAreas((prev) => [...prev, newArea].sort((a, b) => a.displayOrder - b.displayOrder));
      return newArea;
    },
    []
  );

  const updateCurriculumArea = useCallback(
    async (id: string, updates: Partial<CurriculumArea>) => {
      const updated = await updateCurriculumAreaService(id, updates);
      setCurriculumAreas((prev) =>
        prev.map((area) => (area.id === id ? updated : area))
          .sort((a, b) => a.displayOrder - b.displayOrder)
      );
      return updated;
    },
    []
  );

  const deleteCurriculumArea = useCallback(async (id: string) => {
    await deleteCurriculumAreaService(id);
    setCurriculumAreas((prev) => prev.filter((area) => area.id !== id));
  }, []);

  // Level Code CRUD
  const createLevelCode = useCallback(
    async (level: Omit<LevelCode, 'id' | 'createdAt' | 'updatedAt'>) => {
      const newLevel = await createLevelCodeService(level);
      setLevelCodes((prev) => [...prev, newLevel].sort((a, b) => a.sortOrder - b.sortOrder));
      return newLevel;
    },
    []
  );

  const updateLevelCode = useCallback(
    async (id: string, updates: Partial<LevelCode>) => {
      const updated = await updateLevelCodeService(id, updates);
      setLevelCodes((prev) =>
        prev.map((level) => (level.id === id ? updated : level))
          .sort((a, b) => a.sortOrder - b.sortOrder)
      );
      return updated;
    },
    []
  );

  const deleteLevelCode = useCallback(async (id: string) => {
    await deleteLevelCodeService(id);
    setLevelCodes((prev) => prev.filter((level) => level.id !== id));
  }, []);

  // Menu Visibility functions
  const isMenuItemHidden = useCallback(
    (path: string): boolean => {
      return config.hiddenMenuItems?.includes(path) || false;
    },
    [config.hiddenMenuItems]
  );

  const hideMenuItem = useCallback(
    async (path: string) => {
      const currentHidden = config.hiddenMenuItems || [];
      if (!currentHidden.includes(path)) {
        const newHidden = [...currentHidden, path];
        await updateConfig({ hiddenMenuItems: newHidden });
      }
    },
    [config.hiddenMenuItems, updateConfig]
  );

  const showMenuItem = useCallback(
    async (path: string) => {
      const currentHidden = config.hiddenMenuItems || [];
      if (currentHidden.includes(path)) {
        const newHidden = currentHidden.filter((p) => p !== path);
        await updateConfig({ hiddenMenuItems: newHidden });
      }
    },
    [config.hiddenMenuItems, updateConfig]
  );

  const setHiddenMenuItems = useCallback(
    async (items: string[]) => {
      await updateConfig({ hiddenMenuItems: items });
    },
    [updateConfig]
  );

  // Context value
  const value: SchoolConfigContextType = {
    // School Configuration
    config,
    updateConfig,

    // Curriculum Areas
    curriculumAreas,
    curriculumAreaMap,
    createCurriculumArea,
    updateCurriculumArea,
    deleteCurriculumArea,

    // Level Codes
    levelCodes,
    levelCodeMap,
    createLevelCode,
    updateLevelCode,
    deleteLevelCode,

    // Grid Types
    gridTypes,

    // Menu Visibility
    hiddenMenuItems: config.hiddenMenuItems || [],
    isMenuItemHidden,
    hideMenuItem,
    showMenuItem,
    setHiddenMenuItems,

    // Convenience accessors (with fallbacks)
    baseMinutesPerFortnight: config.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
    defaultMinutesPerLine: config.defaultMinutesPerLine || DEFAULT_MINUTES_PER_LINE,
    schoolName: config.schoolName,
    schoolShortName: config.schoolShortName,
    bannerUrl: config.bannerUrl,
    principalName: config.principalName,
    yearLevels: config.yearLevels,

    // Loading state
    loading,
    error,

    // Refresh
    refresh: loadData,
  };

  return (
    <SchoolConfigContext.Provider value={value}>
      {children}
    </SchoolConfigContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access school configuration
 * @throws Error if used outside of SchoolConfigProvider
 */
export function useSchoolConfig(): SchoolConfigContextType {
  const context = useContext(SchoolConfigContext);
  if (context === undefined) {
    throw new Error('useSchoolConfig must be used within a SchoolConfigProvider');
  }
  return context;
}

/**
 * Hook to access school configuration with optional fallback
 * Returns default values if provider is not available (useful during initialization)
 */
export function useSchoolConfigSafe(): SchoolConfigContextType {
  const context = useContext(SchoolConfigContext);

  if (context === undefined) {
    // Return default values when context is not available
    return {
      config: DEFAULT_SCHOOL_CONFIG,
      updateConfig: async () => { throw new Error('SchoolConfigProvider not available'); },
      curriculumAreas: [],
      curriculumAreaMap: new Map(),
      createCurriculumArea: async () => { throw new Error('SchoolConfigProvider not available'); },
      updateCurriculumArea: async () => { throw new Error('SchoolConfigProvider not available'); },
      deleteCurriculumArea: async () => { throw new Error('SchoolConfigProvider not available'); },
      levelCodes: [],
      levelCodeMap: new Map(),
      createLevelCode: async () => { throw new Error('SchoolConfigProvider not available'); },
      updateLevelCode: async () => { throw new Error('SchoolConfigProvider not available'); },
      deleteLevelCode: async () => { throw new Error('SchoolConfigProvider not available'); },
      gridTypes: [],
      hiddenMenuItems: [],
      isMenuItemHidden: () => false,
      hideMenuItem: async () => { throw new Error('SchoolConfigProvider not available'); },
      showMenuItem: async () => { throw new Error('SchoolConfigProvider not available'); },
      setHiddenMenuItems: async () => { throw new Error('SchoolConfigProvider not available'); },
      baseMinutesPerFortnight: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
      defaultMinutesPerLine: DEFAULT_MINUTES_PER_LINE,
      schoolName: DEFAULT_SCHOOL_CONFIG.schoolName,
      schoolShortName: DEFAULT_SCHOOL_CONFIG.schoolShortName,
      bannerUrl: DEFAULT_SCHOOL_CONFIG.bannerUrl,
      principalName: DEFAULT_SCHOOL_CONFIG.principalName,
      yearLevels: DEFAULT_SCHOOL_CONFIG.yearLevels,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }

  return context;
}

// ============================================================================
// Utility hooks for specific values
// ============================================================================

/**
 * Hook to get just the load calculation values
 */
export function useLoadCalculationConfig() {
  const { baseMinutesPerFortnight, defaultMinutesPerLine } = useSchoolConfig();
  return { baseMinutesPerFortnight, defaultMinutesPerLine };
}

/**
 * Hook to get school identity values
 */
export function useSchoolIdentity() {
  const { schoolName, schoolShortName, bannerUrl, principalName, config } = useSchoolConfig();
  return {
    schoolName,
    schoolShortName,
    bannerUrl,
    principalName,
    schoolAddress: config.schoolAddress,
    schoolPhone: config.schoolPhone,
    schoolEmail: config.schoolEmail,
    schoolWebsite: config.schoolWebsite,
    schoolTagline: config.schoolTagline,
    logoUrl: config.logoUrl,
  };
}

/**
 * Hook to get curriculum area by code
 */
export function useCurriculumAreaByCode(code: string): CurriculumArea | undefined {
  const { curriculumAreaMap } = useSchoolConfig();
  return curriculumAreaMap.get(code);
}

/**
 * Hook to get level code by code
 */
export function useLevelCodeByCode(code: string): LevelCode | undefined {
  const { levelCodeMap } = useSchoolConfig();
  return levelCodeMap.get(code);
}
